import { z } from "zod";

import { PASSWORD_REGEX } from "@/constants";
import { UserStatus } from "@/enum";
import { UserLanguage } from "@/enum/user.enum";
import { PeopleSchema } from "@/pages/Private/pages/Apollo/schema/get-people";
import { UserRoleSchema } from "@/pages/Private/pages/UserRoles/schema/roles";
import { ERROR_TYPE } from "@/utils";

const {
	REQUIRED,
	PASSWORD_GUIDELINES,
	INVALID_EMAIL,
	INVALID_VALUE,
	PASSWORD_MISMATCH,
	ACCEPT_TERMS,
} = ERROR_TYPE;

export const RegisterSchema = z
	.object({
		email: z.string().min(1, { message: REQUIRED }).email(INVALID_EMAIL),
		password: z
			.string()
			.min(1, { message: REQUIRED })
			.regex(PASSWORD_REGEX, PASSWORD_GUIDELINES)
			.trim(),
		termsAndConditions: z.boolean().refine((value) => value === true, { message: REQUIRED }),
	})
	.superRefine(({ termsAndConditions }, ctx) => {
		if (!termsAndConditions) {
			ctx.addIssue({
				code: "custom",
				path: ["termsAndConditions"],
				message: ACCEPT_TERMS,
			});
		}
	});

/** The API responds with the same payload as a login — tokens + user. */
export const RegisterRequestSchema = z.object({
	email: z.string().email(INVALID_EMAIL),
	password: z.string().min(8, { message: INVALID_VALUE }),
});

export const SetPasswordSchema = z
	.object({
		password: z
			.string()
			.min(1, { message: REQUIRED })
			.regex(PASSWORD_REGEX, PASSWORD_GUIDELINES)
			.trim(),
		confirmPassword: z.string().min(1, { message: REQUIRED }).trim(),
	})
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				code: "custom",
				path: ["confirmPassword"],
				message: PASSWORD_MISMATCH,
			});
		}
	});

export const LoginRequestSchema = z.object({
	email: z.string().email(INVALID_EMAIL),
	password: z.string().min(8, { message: INVALID_VALUE }),
});

export const CompanySchema = z
	.object({
		id: z.number().gt(0),
		name: z.string().nullable(),
		linkedinUrl: z.string().nullable(),
		hasApolloApiKey: z.boolean(),
	})
	.nullable();

export const FetchMeResponseSchema = z.object({
	id: z.number().gt(0),
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	email: z.string().email(INVALID_EMAIL),
	linkedinUrl: z.string().optional().nullable(),
	is2FAEnabled: z.boolean(),
	isBlocked: z.boolean(),
	status: z.nativeEnum(UserStatus),
	userLanguage: z.nativeEnum(UserLanguage),
	company: CompanySchema,
	roles: z.array(UserRoleSchema),
	prospects: z.array(PeopleSchema).optional(),
	autoAccept: z.boolean().optional(),
	profileVisits: z.boolean().optional(),
	connectionScraping: z.boolean().optional(),
});

export const LoginResponseSchema = z.object({
	accessToken: z.string().optional(),
	otpToken: z.string().optional(),
	user: FetchMeResponseSchema,
});

export const GoogleAuthSchema = z.object({
	clientId: z.string(),
	credential: z.string(),
	select_by: z.string(),
});

export type RegisterType = z.infer<typeof RegisterSchema>;
export type User = z.infer<typeof FetchMeResponseSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
export type GoogleAuth = z.infer<typeof GoogleAuthSchema>;
